import _ from 'lodash';
import angular from 'angular';
import template from './template.html';
import { PRIORITY_VALUES } from '../matched-skills/item';
import '../matched-skills/style.scss';

const splitSkillsByPriority = (skills) => {
    const filterPrioritySkills = [];
    const restPrioritySkills = [];

    (skills || []).forEach((skill) => {
        if (skill.priorityValue === PRIORITY_VALUES.FILTER) {
            filterPrioritySkills.push(skill);
        } else {
            restPrioritySkills.push(skill);
        }
    });

    return { filterPrioritySkills, restPrioritySkills };
};

const getSkillsFitToFreeSpace = (skills, freeSpace) => {
    let index = 0;
    let currentSpace = 0;

    while (currentSpace <= freeSpace && index < skills.length) {
        currentSpace += skills[index].width;
        index += 1;
    }

    if (currentSpace <= freeSpace) {
        index += 1;
    }

    return {
        skillsFitsToFreeSpace: skills.slice(0, index - 1),
        restSkills: skills.slice(index - 1),
    };
};

const getLinesFit = (skillsWithWidth, containerWidth) => {
    let lines = 1;
    let freeSpace = containerWidth;
    let currentIndex = 0;

    while (currentIndex < skillsWithWidth.length) {
        const skillWidth = skillsWithWidth[currentIndex].width;

        if (freeSpace - skillWidth < 0) {
            // eslint-disable-next-line no-plusplus
            lines++;
            freeSpace = containerWidth - skillWidth;
        } else {
            freeSpace -= skillWidth;
        }

        // eslint-disable-next-line no-plusplus
        currentIndex++;
    }

    return {
        lines,
        freeSpace,
    };
};

const getAlwaysShownSkills = _.memoize((skillsWithWidth, containerWidth, alwaysLinesToShow) => {
    const { filterPrioritySkills, restPrioritySkills } = splitSkillsByPriority(skillsWithWidth);
    const { lines, freeSpace } = getLinesFit(filterPrioritySkills, containerWidth);
    let { skillsFitsToFreeSpace, restSkills } = getSkillsFitToFreeSpace(restPrioritySkills, freeSpace);
    let currentLines = lines;

    if (currentLines < alwaysLinesToShow) {
        while (currentLines < alwaysLinesToShow) {
            const {
                skillsFitsToFreeSpace: skillsFitsToFreeSpaceNextLine,
                restSkills: nextRestSkills,
            } = getSkillsFitToFreeSpace(restSkills, containerWidth);

            skillsFitsToFreeSpace = [...skillsFitsToFreeSpace, ...skillsFitsToFreeSpaceNextLine];
            restSkills = nextRestSkills;

            // eslint-disable-next-line no-plusplus
            currentLines++;
        }
    }

    return [...filterPrioritySkills, ...skillsFitsToFreeSpace];
});

function generateSkillsComponents() {
    const skillsContainer = $('<div class="candidate-skills-list" style="display: flex; flex-wrap: wrap;"></div>');

    _.forEach(this.skills, (skill) => {
        const scope = this.$scope.$new();
        scope.$ctrl = {
            skill,
            hideCheckmark: this.hideCheckmark,
        };
        scope.skill = skill;
        const compiledSkillComponent = this.$compile(getSkillTemplate())(scope);

        skillsContainer.append(compiledSkillComponent);
    });

    this.loadComplete = true;
    $(this.$element).find('.candidate-skills-list').remove();
    $(this.$element).find('.skills-render').append(skillsContainer);
    this.$scope.$evalAsync();

    this.$timeout(() => {
        const parentContainer = _.first($(this.$element).find('.skills'));
        if (!parentContainer) {
            return;
        }

        const parentContainerBottomCoordinateInPX = parentContainer.getBoundingClientRect().bottom;
        const renderedSkills = $(skillsContainer).children();

        const indexOfFirstHiddenItem = _.findIndex(renderedSkills, skill => skill.getBoundingClientRect().top >= parentContainerBottomCoordinateInPX);
        this.hiddenItemsCount = (indexOfFirstHiddenItem !== -1) ? this.skills.length - (indexOfFirstHiddenItem + 1) : 0;
        this.recalculateExpandLinkText();
    }, 50);

    function getSkillTemplate() {
        return '<matched-skill-tile skill="::$ctrl.skill" hide-checkmark="::$ctrl.hideCheckmark"></matched-skill-tile>';
    }
}

export const loadingSkillsCount = 50;
export const loadingSkillNormalWidth = 70;

export class MatchedSkillsList {
    defaultLabelKey = 'SKILLS';
    skills = [];
    expanded = null;
    skillsWithWidth = [];
    skillsToPresent = [];
    isLoading = false;
    // generate skills for preview while loading original skills with different width (random used to shake width, to be more natural)
    loadingSkillsWidth = [...Array(loadingSkillsCount)].map((item, index) => (Math.random() * Math.floor(index) + loadingSkillNormalWidth));
    componentIsInited = false;
    hiddenItemsCount = 0;
    loadComplete = false;

    /* @ngInject */
    constructor($scope, $filter, $element, $timeout, $window, $compile, searchSkillsRenderService) {
        this.$scope = $scope;
        this.$filter = $filter;
        this.$element = $element[0];
        this.$timeout = $timeout;
        this.$window = $window;
        this.$compile = $compile;
        this.skillsRenderService = searchSkillsRenderService;
    }

    static isResolvedArray(changes, propName) {
        return changes[propName] && Array.isArray(changes[propName].currentValue);
    }

    $onInit() {
        this.isLoading = true;
    }

    $postLink() {
        this.$timeout(() => {
            this.recalculateSkills();
        }, 500);

        angular.element(this.$window).on('resize', () => {
            this.recalculateSkills();
            this.$scope.$apply();
        });
    }

    defferTileRendering() {
        return generateSkillsComponents.bind(this);
    }

    $onChanges(changes) {
        this.labelKey = this.labelKey || this.defaultLabelKey;
        const isLoadingPrevValue = _.get(changes, 'isLoading.previousValue');
        const isLoadingCurrValue = _.get(changes, 'isLoading.currentValue');

        const areEqual = _.isEqual(_.get(changes, 'skills.previousValue'), _.get(changes, 'skills.currentValue'));

        const skillsPrev = _.get(changes, 'skills.previousValue');
        const skillsCurr = _.get(changes, 'skills.currentValue');

        if (areEqual && _.isArray(skillsCurr) && _.isArray(skillsPrev)) {
            this.recalculateSkills();
            this.componentIsInited = true;
        }

        if (!areEqual) {
            this.recalculateSkills();
            this.componentIsInited = true;
        }

        if (this.componentIsInited) {
            return;
        }

        if (isLoadingPrevValue && !isLoadingCurrValue && (!changes.skills || MatchedSkillsList.isResolvedArray(changes, 'skills'))) {
            this.componentIsInited = true;
            this.recalculateSkills();
        }
    }

    $onDestroy() {
        this.skillsRenderService.removeFromQueue(this.talentId);
        angular.element(this.$window).off('resize');
    }

    toggleExpanded() {
        this.expanded = !this.expanded;
        this.recalculateExpandLinkText();
    }

    recalculateSkills() {
        const skillsBlock = $(this.$element).find('.skills');

        if (!skillsBlock) {
            return;
        }

        const containerWidth = skillsBlock.width() || 0;
        const canShowAllSkills = !containerWidth || this.expanded || this.isLoading;

        if (canShowAllSkills) {
            this.setSkillsToPresent(this.skills);

            return;
        }

        if (this.skillsWithWidth.length === this.skills.length) {
            const alwaysShownSkills = getAlwaysShownSkills(this.skillsWithWidth, containerWidth, 2);

            this.setSkillsToPresent(alwaysShownSkills);

            if (alwaysShownSkills.length < this.skills.length) {
                this.collapse();
            } else {
                this.flushState();
            }
        } else {
            this.setSkillsToPresent(this.skills);
        }

        this.$scope.$evalAsync();
    }

    setSkillsToPresent(skillsToPresent = []) {
        if (this.skillsToPresent == skillsToPresent) {
            return;
        }

        const needToClean = (skillsToPresent.length > 0 && this.skillsToPresent.length && (this.skillsToPresent.length !== skillsToPresent.length));

        if (_.isEmpty(skillsToPresent) && !this.isLoading) {
            this.loadComplete = true;
        } else {
            this.loadComplete = false;
        }

        if (!_.isEmpty(skillsToPresent)) {
            // if (!this.isLoading) {
            //     this.loadComplete = true;
            // }
            this.skillsRenderService.pushToQueue(this.defferTileRendering(), this.talentId, needToClean);
        }
    }

    recalculateExpandLinkText() {
        this.showButtons = this.hiddenItemsCount > 0 || this.expanded !== null;
        this.expandLinkText = this.expanded ? this.$filter('translate')('Less') : `${this.hiddenItemsCount} ${this.$filter('translate')('MORE')}`;
    }

    expand() {
        this.expanded = true;
    }

    collapse() {
        this.expanded = false;
    }

    flushState() {
        this.expanded = null;
    }
}

export const MatchedSkillsListConfiguration = {
    template,
    bindings: {
        talentId: '<',
        skills: '<',
        labelKey: '<',
        isLoading: '<',
        hideCheckmark: '<',
        isFullListView: '<',
    },
    controller: MatchedSkillsList,
    controllerAs: 'matchedSkillsListCtrl',
};

angular.module('TALENYA-FE').component('matchedSkillsListSearch', MatchedSkillsListConfiguration);
